// ./src/components/features/Coupons/CouponGrantRulesEditor/CouponGrantRulesEditor.tsx
//
// The grant-rule rows shared by BOTH coupon grant editors — the event's RSVP rules
// (per person / per group, stored in event.settings) and a ticket type's grants
// (per ticket / per order, stored as ticket_type_coupon rows). The two read the
// same because they ARE the same question: "which voucher, how many, counted per
// what?". Only the wording of the last column and where the rows are persisted
// differ, so those are props and everything else lives here.
//
// Fully controlled: the drawer owns the rows and saves them.

import React from "react";
import { Stack, Grid, NumberInput, SegmentedControl, ActionIcon, Tooltip, Button, Text, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { AppIcon, SelectField } from "@/components/generic";

/** One editable rule row. `key` is a stable client id — a new row has no record yet. */
export interface GrantRuleRow {
  key: string;
  couponTypeId: string;
  quantity: number;
  grantPer: string;
}

export interface CouponGrantRulesEditorProps {
  rows: GrantRuleRow[];
  onChange: (rows: GrantRuleRow[]) => void;
  /** The account's reusable vouchers. */
  typeOptions: { value: string; label: string }[];
  /** What the quantity is counted per — 2–3 options (a SegmentedControl, not a dropdown). */
  perOptions: { value: string; label: string }[];
  /** Default grantPer for a newly added row (defaults to the first per option). */
  defaultGrantPer?: string;
  /** Skeleton rows while the existing rules load. */
  loading?: boolean;
  skeletonRows?: number;
  emptyText?: string;
  addLabel?: string;
  /** Explains what each per option counts. */
  hint?: React.ReactNode;
  disabled?: boolean;
  /**
   * Server field errors for the row at index i, keyed by column
   * (`{ 0: { couponTypeId: "Coupon type not found" } }`). The refusal is drawn on the
   * offending control — with several rows on screen, a footer message can't say which.
   */
  rowErrors?: Record<number, Record<string, string>>;
  /** A refusal that belongs to the rule set as a whole (a settings blob the server rejected). */
  error?: string | null;
}

const newKey = (): string =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `row-${Date.now()}-${Math.random()}`;

export const emptyGrantRow = (grantPer: string): GrantRuleRow => ({
  key: newKey(),
  couponTypeId: "",
  quantity: 1,
  grantPer,
});

const CouponGrantRulesEditorComponent: React.FC<CouponGrantRulesEditorProps> = ({
  rows,
  onChange,
  typeOptions,
  perOptions,
  defaultGrantPer,
  loading = false,
  skeletonRows = 2,
  emptyText,
  addLabel,
  hint,
  disabled = false,
  rowErrors,
  error,
}) => {
  const { t } = useTranslation(["coupons", "common"]);

  const fallbackPer = defaultGrantPer ?? perOptions[0]?.value ?? "";

  const updateAt = (index: number, patch: Partial<GrantRuleRow>) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const removeAt = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  // Column labels live in ONE header row — hanging them on the first row's inputs
  // makes that row taller and the trash icon stops lining up with the rest.
  const renderHeader = () => (
    <Grid gutter="xs" align="center" px={2}>
      <Grid.Col span={5}>
        <Text size="xs" c="dimmed" fw={600}>
          {t("coupons:rules.columns.voucher", { defaultValue: "Voucher" })}
        </Text>
      </Grid.Col>
      <Grid.Col span={2}>
        <Text size="xs" c="dimmed" fw={600}>
          {t("coupons:rules.columns.quantity", { defaultValue: "How many" })}
        </Text>
      </Grid.Col>
      <Grid.Col span={4}>
        <Text size="xs" c="dimmed" fw={600}>
          {t("coupons:rules.columns.per", { defaultValue: "For each" })}
        </Text>
      </Grid.Col>
      <Grid.Col span={1} />
    </Grid>
  );

  // Matches the real row's shape (same spans, same control heights) so the list
  // doesn't jump when the rules arrive.
  const renderSkeletonRow = (index: number) => (
    <Grid key={`sk-${index}`} gutter="xs" align="center">
      <Grid.Col span={5}>
        <Skeleton height={36} radius="sm" />
      </Grid.Col>
      <Grid.Col span={2}>
        <Skeleton height={36} radius="sm" />
      </Grid.Col>
      <Grid.Col span={4}>
        <Skeleton height={36} radius="sm" />
      </Grid.Col>
      <Grid.Col span={1}>
        <Skeleton height={28} circle />
      </Grid.Col>
    </Grid>
  );

  const renderRow = (row: GrantRuleRow, index: number) => {
    const rowError = rowErrors?.[index];
    // Centred while the row is clean (so the trash icon lines up with the inputs), but
    // TOP-aligned the moment a message appears under one control — otherwise its
    // row-mates slide down and the row looks broken.
    const hasRowError = !!rowError && Object.values(rowError).some(Boolean);

    return (
    <Grid key={row.key} gutter="xs" align={hasRowError ? "flex-start" : "center"}>
      <Grid.Col span={5}>
        <SelectField
          placeholder={t("coupons:rules.pickVoucher", { defaultValue: "Pick a voucher" })}
          data={typeOptions}
          value={row.couponTypeId || null}
          onChange={(_name, v) => updateAt(index, { couponTypeId: v ?? "" })}
          disabled={disabled}
          searchable
          comboboxProps={DRAWER_COMBOBOX_PROPS}
          size="md"
          error={rowError?.couponTypeId}
        />
      </Grid.Col>
      <Grid.Col span={2}>
        <NumberInput
          value={row.quantity}
          onChange={(v) => updateAt(index, { quantity: Math.max(1, Number(v) || 1) })}
          min={1}
          max={99}
          clampBehavior="strict"
          allowNegative={false}
          allowDecimal={false}
          disabled={disabled}
          size="md"
          aria-label={t("coupons:rules.columns.quantity", { defaultValue: "How many" })}
          error={rowError?.quantity}
        />
      </Grid.Col>
      <Grid.Col span={4}>
        <SegmentedControl
          fullWidth
          size="md"
          value={row.grantPer || fallbackPer}
          onChange={(v) => updateAt(index, { grantPer: v })}
          disabled={disabled}
          data={perOptions}
        />
        {rowError?.grantPer && (
          <Text size="xs" c="red" mt={4}>
            {rowError.grantPer}
          </Text>
        )}
      </Grid.Col>
      <Grid.Col span={1}>
        <Tooltip label={t("common:buttons.remove", { defaultValue: "Remove" })} withArrow position="top">
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => removeAt(index)}
            disabled={disabled}
            aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      </Grid.Col>
    </Grid>
    );
  };

  if (loading) {
    return (
      <Stack gap="md">
        {renderHeader()}
        {Array.from({ length: skeletonRows }, (_, i) => renderSkeletonRow(i))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {rows.length > 0 && renderHeader()}

      {rows.map((row, index) => renderRow(row, index))}

      {rows.length === 0 && (
        <Text size="sm" c="dimmed">
          {emptyText ?? t("coupons:rules.empty", { defaultValue: "No rules yet — add one below." })}
        </Text>
      )}

      {/* A refusal about the rule set as a whole belongs here, next to the rows it is
          about, not only in the footer alert the host may have scrolled past. */}
      {error && (
        <Text size="sm" c="red">
          {error}
        </Text>
      )}

      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}

      <Button
        variant="light"
        size="md"
        onClick={() => onChange([...rows, emptyGrantRow(fallbackPer)])}
        disabled={disabled}
        leftSection={<AppIcon iconName="Plus" size={14} />}
        style={{ alignSelf: "flex-start" }}
      >
        {addLabel ?? t("coupons:rules.add", { defaultValue: "Add rule" })}
      </Button>
    </Stack>
  );
};

export const CouponGrantRulesEditor = React.memo(CouponGrantRulesEditorComponent);
